import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { fmtEUR } from "../lib/format";

export default function AdvPage() {
  const { stores, storeId, from, to, isMaster } = useOutletContext();
  const [data, setData] = useState(null);
  const [giorno, setGiorno] = useState({ data: "", importo: "", note: "" });
  const [periodo, setPeriodo] = useState({ data_inizio: "", data_fine: "", importo_totale: "", note: "" });
  const effStore = storeId !== "all" ? storeId : stores[0]?.id;

  const load = () => {
    api.get(`/adv?store_id=${storeId}&from=${from}&to=${to}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { setData(null); load(); }, [storeId, from, to]);

  const addGiorno = async () => {
    try {
      await api.post("/adv", { store_id: effStore, data: giorno.data, importo: parseFloat(String(giorno.importo).replace(",", ".")), note: giorno.note });
      toast.success("Spesa registrata");
      setGiorno({ data: "", importo: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addPeriodo = async () => {
    try {
      const res = await api.post("/adv/periodo", {
        store_id: effStore, data_inizio: periodo.data_inizio, data_fine: periodo.data_fine,
        importo_totale: parseFloat(String(periodo.importo_totale).replace(",", ".")), note: periodo.note,
      });
      toast.success(`Ripartiti su ${res.data.giorni} giorni`);
      setPeriodo({ data_inizio: "", data_fine: "", importo_totale: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    try { await api.delete(`/adv/${id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const inp = "border border-slate-300 rounded-md px-2 py-1.5 text-sm w-full";

  return (
    <div data-testid="adv-page" className="space-y-6">
      <div className="flex items-baseline gap-4">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Spesa pubblicitaria (ADV)</h1>
        {data && <span data-testid="adv-totale" className="text-sm text-slate-500">Totale periodo: <span className="font-mono font-bold text-slate-900">{fmtEUR(data.totale)}</span></span>}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Inserimento giornaliero</h2>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="adv-data" type="date" className={inp} value={giorno.data} onChange={(e) => setGiorno({ ...giorno, data: e.target.value })} />
            <input data-testid="adv-importo" placeholder="Importo €" className={inp} value={giorno.importo} onChange={(e) => setGiorno({ ...giorno, importo: e.target.value })} />
            <input data-testid="adv-note" placeholder="Note" className={inp} value={giorno.note} onChange={(e) => setGiorno({ ...giorno, note: e.target.value })} />
          </div>
          <button data-testid="adv-add-button" onClick={addGiorno} disabled={!giorno.data || !giorno.importo || !effStore}
            className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-40">Aggiungi giorno</button>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Inserimento per periodo (ripartizione automatica sui giorni)</h2>
          <div className="grid grid-cols-3 gap-2">
            <input data-testid="adv-periodo-inizio" type="date" className={inp} value={periodo.data_inizio} onChange={(e) => setPeriodo({ ...periodo, data_inizio: e.target.value })} />
            <input data-testid="adv-periodo-fine" type="date" className={inp} value={periodo.data_fine} onChange={(e) => setPeriodo({ ...periodo, data_fine: e.target.value })} />
            <input data-testid="adv-periodo-totale" placeholder="Importo totale €" className={inp} value={periodo.importo_totale} onChange={(e) => setPeriodo({ ...periodo, importo_totale: e.target.value })} />
          </div>
          <button data-testid="adv-periodo-button" onClick={addPeriodo} disabled={!periodo.data_inizio || !periodo.data_fine || !periodo.importo_totale || !effStore}
            className="mt-3 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-md px-4 py-1.5 disabled:opacity-40">Ripartisci sul periodo</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table data-testid="adv-table" className="w-full text-sm">
          <thead>
            <tr className="bg-slate-100/80 text-left">
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600">Data</th>
              {isMaster && <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600">Store</th>}
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">Importo</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600">Origine</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600">Note</th>
              <th className="px-3 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-600">Modificato da</th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {(data?.voci || []).map((v) => (
              <tr key={v.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono">{v.data}</td>
                {isMaster && <td className="px-3 py-2 text-xs text-slate-500">{stores.find((s) => s.id === v.store_id)?.nome}</td>}
                <td className="px-3 py-2 font-mono text-right tnum">{fmtEUR(v.importo)}</td>
                <td className="px-3 py-2">
                  <span className={`text-xs rounded px-1.5 py-0.5 border ${v.origine === "manuale" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-sky-50 text-sky-700 border-sky-200"}`}>
                    {v.origine}
                  </span>
                  {v.origine === "manuale" && v.valore_esterno != null && v.valore_esterno !== v.importo && (
                    <span className="ml-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5">API: {fmtEUR(v.valore_esterno)}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">{v.note}</td>
                <td className="px-3 py-2 text-xs text-slate-400">{v.aggiornato_da}</td>
                <td className="px-3 py-2 text-right">
                  <button data-testid="adv-delete-button" onClick={() => del(v.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {data && data.voci.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Nessuna spesa nel periodo. Senza ADV non è possibile calcolare guadagno netto e ROAS.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-400">Il dato inserito a mano (origine <em>manuale</em>) è quello ufficiale: una futura sincronizzazione Meta non lo sovrascriverà senza conferma esplicita (vedi schermata Divergenze).</p>
    </div>
  );
}
